// Evidence Chat API (ported from FRaiME's chatApi.ts, minus web search and GraphRAG v2).
import { api } from "@/services/api";
import { getEnv } from "@/config/env";

const API_BASE_URL = getEnv("VITE_API_BASE_URL") || "http://localhost:8010";

// The SSE stream is read with fetch (api.ts has no streaming), so it needs the
// token getter too. AuthProvider registers it alongside api.ts's.
let tokenGetter: (() => Promise<string | null>) | null = null;
export function setChatStreamTokenGetter(fn: () => Promise<string | null>) {
  tokenGetter = fn;
}

// ── API response shapes (snake_case from the backend) ────────────────────────

interface ChatSessionApiResponse {
  id: string;
  project_id: string;
  created_by_user_id: string | null;
  created_by_email: string | null;
  title: string;
  metadata_filters: Record<string, string[]> | null;
  created_at: string;
  updated_at: string;
  last_message_at: string | null;
  is_owner: boolean;
}

/** A content-intelligence citation: the passage, its file and its page anchor. */
export interface ChatCitation {
  id: string;
  content: string;
  sourcefile: string;
  /** A bare page number, or "<file>#page=N". */
  sourcepage: string;
}

// "interrupted": the stream was cut off (Stop, refresh, tab close) and `content`
// is the partial answer the backend saved. Null: the turn completed.
export type MessageStatus = "interrupted";

/** One selection in effect at send time; `label` is snapshotted for history display. */
export interface AppliedFilterItem {
  key: string;
  label: string;
  values: string[];
}

/** Snapshot stored on the assistant turn: which documents it searched. */
export interface AppliedFilters {
  items: AppliedFilterItem[];
  matchedCount: number;
  totalCount: number;
}

interface AppliedFiltersApiResponse {
  items: AppliedFilterItem[];
  matched_count: number;
  total_count: number;
}

function toAppliedFilters(r: AppliedFiltersApiResponse | null | undefined): AppliedFilters | null {
  if (!r) return null;
  return { items: r.items ?? [], matchedCount: r.matched_count, totalCount: r.total_count };
}

function fromAppliedFilters(f: AppliedFilters | null | undefined): AppliedFiltersApiResponse | null {
  if (!f) return null;
  return { items: f.items, matched_count: f.matchedCount, total_count: f.totalCount };
}

interface ChatMessageApiResponse {
  id: string;
  session_id: string;
  role: "user" | "assistant" | "system";
  content: string;
  sender_user_id: string | null;
  sender_email: string | null;
  citations: ChatCitation[] | null;
  applied_filters: AppliedFiltersApiResponse | null;
  thinking: string | null;
  status: MessageStatus | null;
  created_at: string;
}

interface PaginatedMessagesApiResponse {
  items: ChatMessageApiResponse[];
  next_cursor: string | null;
}

// ── Camel-case views ─────────────────────────────────────────────────────────

export interface ChatSession {
  id: string;
  projectId: string;
  createdByUserId: string | null;
  createdByEmail: string | null;
  title: string;
  /** Saved selection; Evidence Chat keeps `{ document_ids: [...] }`. Null: all documents. */
  metadataFilters: Record<string, string[]> | null;
  createdAt: string;
  updatedAt: string;
  lastMessageAt: string | null;
  isOwner: boolean;
}

export interface ChatMessage {
  id: string;
  sessionId: string;
  role: "user" | "assistant" | "system";
  content: string;
  senderUserId: string | null;
  senderEmail: string | null;
  citations: ChatCitation[] | null;
  appliedFilters: AppliedFilters | null;
  thinking: string | null;
  status: MessageStatus | null;
  createdAt: string;
  /** Retrieval route from content-intelligence's "routing" status; client-side only. */
  route: string | null;
}

export interface PaginatedMessages {
  items: ChatMessage[];
  nextCursor: string | null;
}

function toSession(r: ChatSessionApiResponse): ChatSession {
  return {
    id: r.id,
    projectId: r.project_id,
    createdByUserId: r.created_by_user_id,
    createdByEmail: r.created_by_email,
    title: r.title,
    metadataFilters: r.metadata_filters,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
    lastMessageAt: r.last_message_at,
    isOwner: r.is_owner,
  };
}

function toMessage(r: ChatMessageApiResponse): ChatMessage {
  return {
    id: r.id,
    sessionId: r.session_id,
    role: r.role,
    content: r.content,
    senderUserId: r.sender_user_id,
    senderEmail: r.sender_email,
    citations: r.citations,
    appliedFilters: toAppliedFilters(r.applied_filters),
    thinking: r.thinking,
    status: r.status ?? null,
    createdAt: r.created_at,
    route: null,
  };
}

export const chatApi = {
  listSessions: async (projectId: string): Promise<ChatSession[]> => {
    const data = await api.get<ChatSessionApiResponse[]>(`/api/projects/${encodeURIComponent(projectId)}/chat-sessions`);
    return data.map(toSession);
  },

  createSession: async (projectId: string, title?: string): Promise<ChatSession> => {
    const data = await api.post<ChatSessionApiResponse>(`/api/projects/${encodeURIComponent(projectId)}/chat-sessions`, {
      title: title ?? null,
    });
    return toSession(data);
  },

  renameSession: async (sessionId: string, title: string): Promise<ChatSession> => {
    const data = await api.patch<ChatSessionApiResponse>(`/api/chat-sessions/${sessionId}`, { title });
    return toSession(data);
  },

  updateSessionFilters: async (
    sessionId: string,
    metadataFilters: Record<string, string[]> | null,
  ): Promise<ChatSession> => {
    const data = await api.patch<ChatSessionApiResponse>(`/api/chat-sessions/${sessionId}/metadata-filters`, {
      metadata_filters: metadataFilters,
    });
    return toSession(data);
  },

  deleteSession: async (sessionId: string): Promise<void> => {
    await api.delete(`/api/chat-sessions/${sessionId}`);
  },

  listMessages: async (
    sessionId: string,
    opts: { cursor?: string | null; limit?: number } = {},
  ): Promise<PaginatedMessages> => {
    const params = new URLSearchParams();
    if (opts.cursor) params.set("cursor", opts.cursor);
    if (opts.limit) params.set("limit", String(opts.limit));
    const qs = params.toString();
    const data = await api.get<PaginatedMessagesApiResponse>(
      `/api/chat-sessions/${sessionId}/messages${qs ? `?${qs}` : ""}`,
    );
    return { items: data.items.map(toMessage), nextCursor: data.next_cursor };
  },

  /** Send a turn and read the SSE reply, dispatching each event to `handlers`. */
  streamMessage: async (
    sessionId: string,
    content: string,
    handlers: ChatStreamHandlers,
    options: { documentIds?: string[]; appliedFilters?: AppliedFilters | null; signal?: AbortSignal } = {},
  ): Promise<void> => {
    const token = tokenGetter ? await tokenGetter() : null;
    if (!token) throw new Error("Not authenticated");

    const body: Record<string, unknown> = { content };
    if (options.documentIds) body.document_ids = options.documentIds;
    if (options.appliedFilters) body.applied_filters = fromAppliedFilters(options.appliedFilters);
    const response = await fetch(`${API_BASE_URL}/api/chat-sessions/${sessionId}/messages/stream`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        Accept: "text/event-stream",
      },
      body: JSON.stringify(body),
      signal: options.signal,
    });

    if (!response.ok || !response.body) {
      const errorBody = await response.json().catch(() => ({}));
      throw new Error(errorBody.detail || `API error: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE events are separated by a blank line.
      let sep = buffer.indexOf("\n\n");
      while (sep !== -1) {
        const rawEvent = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        sep = buffer.indexOf("\n\n");
        if (dispatchRawEvent(rawEvent, handlers)) return;
      }
    }
    handlers.onDone?.();
  },
};

// ── Streaming event types ────────────────────────────────────────────────────

export type ChatStreamEvent =
  | { type: "user_message"; message: ChatMessageApiResponse }
  | { type: "citations"; items: ChatCitation[] }
  | { type: "thinking"; content: string }
  | { type: "delta"; content: string }
  | { type: "assistant_message"; message: ChatMessageApiResponse }
  | { type: "session_update"; session: { id: string; title: string } }
  | { type: "status"; data: { phase: string; route?: string; message?: string } }
  | { type: "error"; detail: string; status?: number };

export interface ChatStreamHandlers {
  onUserMessage?: (message: ChatMessage) => void;
  onCitations?: (items: ChatCitation[]) => void;
  onThinking?: (text: string) => void;
  onDelta?: (text: string) => void;
  onAssistantMessage?: (message: ChatMessage) => void;
  onSessionUpdate?: (update: { id: string; title: string }) => void;
  onStatus?: (data: { phase: string; route?: string; message?: string }) => void;
  onError?: (detail: string, status?: number) => void;
  onDone?: () => void;
}

/** Handle one SSE event block; returns true on the [DONE] sentinel. */
export function dispatchRawEvent(rawEvent: string, handlers: ChatStreamHandlers): boolean {
  for (const line of rawEvent.split("\n")) {
    if (!line.startsWith("data:")) continue;
    const data = line.slice("data:".length).trim();
    if (!data) continue;
    if (data === "[DONE]") {
      handlers.onDone?.();
      return true;
    }
    let parsed: ChatStreamEvent;
    try {
      parsed = JSON.parse(data) as ChatStreamEvent;
    } catch {
      continue;
    }
    dispatchStreamEvent(parsed, handlers);
  }
  return false;
}

function dispatchStreamEvent(event: ChatStreamEvent, h: ChatStreamHandlers) {
  switch (event.type) {
    case "user_message":
      h.onUserMessage?.(toMessage(event.message));
      return;
    case "citations":
      h.onCitations?.(event.items);
      return;
    case "thinking":
      h.onThinking?.(event.content);
      return;
    case "delta":
      h.onDelta?.(event.content);
      return;
    case "assistant_message":
      h.onAssistantMessage?.(toMessage(event.message));
      return;
    case "session_update":
      h.onSessionUpdate?.(event.session);
      return;
    case "status":
      h.onStatus?.(event.data);
      return;
    case "error":
      h.onError?.(event.detail, event.status);
      return;
  }
}
